/**
 * TRACK 2 — Field Operations Dashboard.
 *
 * 5:30 AM command center. Single page that answers "what needs attention
 * today" without making the operator hunt across surfaces.
 *
 * Pulls existing endpoints — no new backend routes, no new collections.
 * Each row deep-links to the canonical resolution surface.
 *
 *   Open Revisions             → /projects/:id/revisions
 *   Open Punch Items           → /projects/:id (punch tab)
 *   Open Trust Failures        → /projects/:id (trust summary)
 *   Open Acknowledgements      → /projects/:id/revisions
 *   Open RFIs                  → backlog (no RFI surface yet)
 *   Open Safety Actions        → backlog (no safety surface yet)
 *   Open Findings              → /projects/:id/intake
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, ChevronRight, Clock, FileWarning } from "lucide-react";
import { api } from "@/lib/api";
import { workspace } from "@/lib/workspace";
import MorningRollCall from "@/components/MorningRollCall";
import StoryDrawer from "@/components/stories/StoryDrawer";

function _stateColor(count, severity) {
  if (count === 0) return "var(--fop-text-3)";
  if (severity === "critical") return "var(--st-escalated, #ef4444)";
  if (severity === "warning") return "var(--st-pending, #f59e0b)";
  return "var(--fop-primary)";
}

function OperationsRow({ label, count, hint, severity, to, testid, unavailable, onRetry }) {
  // R-09 (independent recheck, 2026-10-01) — original GH-28 reopened.
  // A failed dependency must never render as a confident, "verified"
  // zero — that is indistinguishable from "we checked, there's nothing
  // outstanding" and can let a real backlog go unnoticed. `unavailable`
  // short-circuits straight to an explicit error state with retry,
  // bypassing the normal count/hint/severity rendering entirely.
  if (unavailable) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 border-b border-[var(--fop-border)]"
        data-testid={testid}
        data-unavailable="true"
      >
        <AlertTriangle size={16} className="shrink-0" style={{ color: "var(--st-pending, #f59e0b)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--fop-text)] font-semibold">{label}</div>
          <div className="text-[11px] text-[var(--st-pending,#f59e0b)]">Couldn't load — status unknown, not verified clear</div>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface)] shrink-0"
          data-testid={`${testid}-retry`}
        >
          Retry
        </button>
      </div>
    );
  }
  const color = _stateColor(count, severity);
  const Cmp = to ? Link : "div";
  const cmpProps = to ? { to } : {};
  return (
    <Cmp
      {...cmpProps}
      className="flex items-center gap-3 px-4 py-3 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] transition-colors"
      data-testid={testid}
    >
      <div
        className="font-mono text-xl font-bold tabular-nums w-12 text-right"
        style={{ color }}
        data-testid={`${testid}-count`}
      >
        {count}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[var(--fop-text)] font-semibold">{label}</div>
        {hint && <div className="text-[11px] text-[var(--fop-text-3)] truncate">{hint}</div>}
      </div>
      {to && count > 0 && (
        <ChevronRight size={16} className="text-[var(--fop-text-3)] shrink-0" />
      )}
    </Cmp>
  );
}


// Track 2B — Story-driven row. Click expands a list of the underlying
// stories in a side drawer, so every number is explainable and clickable.
function OperationsStoryRow({ label, count, hint, severity, filter, projectId, testid, unavailable, onRetry }) {
  const [open, setOpen] = useState(false);
  const [stories, setStories] = useState(null);
  const [storiesError, setStoriesError] = useState(false);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [storyId, setStoryId] = useState(null);
  const color = _stateColor(count, severity);

  // R-09 — same "unavailable, not a verified zero" treatment as
  // OperationsRow, since this component has its own separate
  // count/hint/severity rendering path.
  if (unavailable) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 border-b border-[var(--fop-border)]"
        data-testid={testid}
        data-unavailable="true"
      >
        <AlertTriangle size={16} className="shrink-0" style={{ color: "var(--st-pending, #f59e0b)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--fop-text)] font-semibold">{label}</div>
          <div className="text-[11px] text-[var(--st-pending,#f59e0b)]">Couldn't load — status unknown, not verified clear</div>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface)] shrink-0"
          data-testid={`${testid}-retry`}
        >
          Retry
        </button>
      </div>
    );
  }

  const loadList = () => {
    if (!projectId) return;
    setStoriesLoading(true);
    setStoriesError(false);
    api.get(`/projects/${projectId}/stories${filter && filter !== "all" ? `?status=${filter}` : ""}`)
      .then((r) => { setStories(r.data?.items || []); setStoriesLoading(false); })
      .catch(() => { setStoriesError(true); setStoriesLoading(false); });
  };
  const onToggle = () => {
    if (!count) return;
    // B2-06 (independent recheck batch 2, 2026-10-01) — a failed load
    // used to be caught as `setStories([])`, which is truthy, so this
    // `!stories` guard silently blocked every retry forever: the user
    // could open/close the row as many times as they liked and it
    // would never fetch again. Track the error separately so a failed
    // fetch is always retried on the next click, same as a null one.
    if (!open && (!stories || storiesError) && !storiesLoading) loadList();
    setOpen((o) => !o);
  };
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] transition-colors text-left"
        data-testid={testid}
        disabled={!count}
      >
        <div
          className="font-mono text-xl font-bold tabular-nums w-12 text-right"
          style={{ color }}
          data-testid={`${testid}-count`}
        >
          {count}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[var(--fop-text)] font-semibold">{label}</div>
          {hint && <div className="text-[11px] text-[var(--fop-text-3)] truncate">{hint}</div>}
        </div>
        {count > 0 && (
          <ChevronRight
            size={16}
            className={`text-[var(--fop-text-3)] shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          />
        )}
      </button>
      {open && storiesLoading && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] text-[12px] text-[var(--fop-text-3)] flex items-center gap-2" data-testid={`${testid}-loading`}>
          Loading stories…
        </div>
      )}
      {open && storiesError && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)] flex items-center gap-3" data-testid={`${testid}-error`}>
          <span className="text-[12px] text-[var(--st-pending,#f59e0b)]">Couldn't load — try again</span>
          <button
            type="button"
            onClick={loadList}
            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface)]"
            data-testid={`${testid}-error-retry`}
          >
            Retry
          </button>
        </div>
      )}
      {open && stories && stories.length > 0 && (
        <div className="px-4 py-2 border-b border-[var(--fop-border)] bg-[var(--fop-surface-2)]" data-testid={`${testid}-list`}>
          <ul className="text-[12px] space-y-1">
            {stories.slice(0, 10).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setStoryId(s.id)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--fop-surface)] text-[var(--fop-text)]"
                  data-testid={`${testid}-list-item-${s.id}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
                    {s.sheet_number || "—"}
                  </span>
                  <span className="flex-1 min-w-0 truncate">
                    {s.status_label}{s.people?.[0] ? ` · ${s.people[0]}` : ""}
                  </span>
                  <ChevronRight size={11} className="text-[var(--fop-text-3)]" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <StoryDrawer
        open={!!storyId}
        storyId={storyId}
        onClose={() => setStoryId(null)}
      />
    </>
  );
}

export default function OperationsDashboard() {
  const navigate = useNavigate();
  const lastProject = workspace.getLastProject();
  const projectId = lastProject?.project_id || null;
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(projectId);
  const [punch, setPunch] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [trust, setTrust] = useState(null);
  const [findings, setFindings] = useState(null);
  const [warRoom, setWarRoom] = useState(null);
  const [storyCounts, setStoryCounts] = useState(null);
  const [assetIntel, setAssetIntel] = useState(null);
  const [storyDrawerTarget, setStoryDrawerTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  // R-09 (independent recheck, 2026-10-01) — original GH-28 reopened.
  // Tracks which of the 7 parallel dependency fetches actually
  // succeeded, independent of the (possibly null/[]) data itself, so
  // the UI can tell "verified zero" apart from "unavailable, unknown".
  const [fetchStatus, setFetchStatus] = useState({});
  const [retryTick, setRetryTick] = useState(0);
  const retry = useCallback(() => setRetryTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    api.get("/projects").then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data : r.data?.projects || [];
      setProjects(list);
      if (!selectedId && list.length) setSelectedId(list[0].id);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setLoading(true);
    Promise.allSettled([
      api.get(`/projects/${selectedId}/punch-items/summary`),
      api.get(`/projects/${selectedId}/revisions`),
      api.get(`/projects/${selectedId}/trust`),
      // GH-28 (real-plan audit, 2026-10-01) — this called a nonexistent
      // `/intake/groups` endpoint with a `groups` param; the real
      // endpoint is `/search/global` and its param is `types`, not
      // `groups`. `q: ""` now means "browse all" (backend GH-28 fix).
      api.get(`/search/global`, { params: { project_id: selectedId, q: "", types: "findings" } }),
      api.get(`/projects/${selectedId}/revision-war-room`),
      api.get(`/projects/${selectedId}/stories/counts`),
      api.get(`/projects/${selectedId}/assets/intelligence`),
    ]).then((results) => {
      if (!alive) return;
      const [pRes, rRes, tRes, fRes, wRes, sRes, aRes] = results;
      setPunch(pRes.status === "fulfilled" ? pRes.value.data : null);
      setRevisions(rRes.status === "fulfilled"
        ? (Array.isArray(rRes.value.data) ? rRes.value.data : rRes.value.data?.revisions || [])
        : []);
      setTrust(tRes.status === "fulfilled" ? tRes.value.data : null);
      setFindings(fRes.status === "fulfilled" ? fRes.value.data : null);
      setWarRoom(wRes.status === "fulfilled" ? wRes.value.data : null);
      setStoryCounts(sRes.status === "fulfilled" ? sRes.value.data : null);
      setAssetIntel(aRes.status === "fulfilled" ? aRes.value.data : null);
      // R-09 — record success/failure PER dependency, independent of
      // the (deliberately null/[] on failure) data above, so rows can
      // tell "verified zero" apart from "couldn't load".
      setFetchStatus({
        punch: pRes.status, revisions: rRes.status, trust: tRes.status,
        findings: fRes.status, warRoom: wRes.status, storyCounts: sRes.status,
        assetIntel: aRes.status,
      });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selectedId, retryTick]);

  const openPunch = punch?.open_count || 0;
  const openRevisions = revisions.filter((r) => !r.acknowledged_at && r.status !== "closed").length;
  const ackCount = (trust?.counts?.sheets_superseded || 0);
  const trustFailures = (trust?.messages || []).length;
  const openFindings = (findings?.counts?.findings || 0);

  // Pillar 5 — War Room signal counts.
  const wrSeverity = warRoom?.by_severity || {};
  const wrAck = warRoom?.ack_counts || {};
  const criticalChanges = wrSeverity.critical || 0;
  const highChanges = wrSeverity.high || 0;
  const reviewsNeeded = (warRoom?.rows || []).filter((r) => !r.review && r.changes_total > 0).length;
  const fieldNotifyNeeded = (warRoom?.rows || []).filter((r) => r.review && !r.last_send && r.changes_total > 0).length;
  const ackNeeded = wrAck.pending || 0;
  // Pillar 6 — inbox signal counts.
  const inboxClarifications = wrAck.needs_clarification || 0;
  const inboxConflicts = wrAck.field_conflict || 0;

  // R-09 — per-dependency "did this actually load" flags, read by the
  // JSX below instead of inferring failure from a falsy/zero value.
  const punchUnavailable = fetchStatus.punch === "rejected";
  const revisionsUnavailable = fetchStatus.revisions === "rejected";
  const trustUnavailable = fetchStatus.trust === "rejected";
  const findingsUnavailable = fetchStatus.findings === "rejected";
  const warRoomUnavailable = fetchStatus.warRoom === "rejected";
  const storiesUnavailable = fetchStatus.storyCounts === "rejected";
  const assetsUnavailable = fetchStatus.assetIntel === "rejected";

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedId),
    [projects, selectedId],
  );

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="operations-dashboard">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--fop-primary)] mb-1">
          Field Operations
        </div>
        <h1 className="text-3xl font-semibold text-[var(--fop-text)]">
          What needs attention today
        </h1>
        <div className="mt-2 text-sm text-[var(--fop-text-2)]">
          5:30 AM command center · counts refresh on open
        </div>
      </div>

      {/* Project picker (small — most operators have 1-3 active projects) */}
      <div className="mb-4 flex items-center gap-2">
        <label className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
          Project
        </label>
        <select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded px-2 py-1 text-sm text-[var(--fop-text)]"
          data-testid="ops-project-picker"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="fop-surface-2 rounded-lg border border-[var(--fop-border)] overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[var(--fop-text-3)]" data-testid="ops-loading">
            Loading the day…
          </div>
        ) : (
          <>
            {/* Pillar 5 — Morning Roll Call banner */}
            {selectedId && (
              <div className="p-3 border-b border-[var(--fop-border)]">
                <MorningRollCall projectId={selectedId} testid="ops-morning-roll-call" />
              </div>
            )}
            {/* Track 2B — Story-driven counts (canonical source of truth). */}
            {selectedId && (storyCounts?.buckets || storiesUnavailable) && (
              <>
                <OperationsStoryRow
                  label="Needs Attention"
                  count={storyCounts?.buckets?.needs_attention || 0}
                  hint={storyCounts?.buckets?.needs_attention ? "Field activity recorded but not yet routed" : "Nothing in needs-attention"}
                  severity={(storyCounts?.buckets?.needs_attention || 0) > 0 ? "warning" : null}
                  filter="needs_attention" projectId={selectedId}
                  testid="ops-stories-needs-attention"
                  unavailable={storiesUnavailable} onRetry={retry}
                />
                <OperationsStoryRow
                  label="Conflict Open"
                  count={storyCounts?.buckets?.conflict_open || 0}
                  hint={storyCounts?.buckets?.conflict_open ? "Field reports the drawing doesn't match" : "No open field conflicts"}
                  severity={(storyCounts?.buckets?.conflict_open || 0) > 0 ? "critical" : null}
                  filter="conflict_open" projectId={selectedId}
                  testid="ops-stories-conflict-open"
                  unavailable={storiesUnavailable} onRetry={retry}
                />
                <OperationsStoryRow
                  label="Waiting For Field"
                  count={storyCounts?.buckets?.waiting_for_field || 0}
                  hint={storyCounts?.buckets?.waiting_for_field ? "Sent to foreman, no response yet" : "Field has acted on every send"}
                  severity={(storyCounts?.buckets?.waiting_for_field || 0) > 0 ? "warning" : null}
                  filter="waiting_for_field" projectId={selectedId}
                  testid="ops-stories-waiting-field"
                  unavailable={storiesUnavailable} onRetry={retry}
                />
                <OperationsStoryRow
                  label="Waiting For Clarification"
                  count={storyCounts?.buckets?.waiting_for_clarification || 0}
                  hint={storyCounts?.buckets?.waiting_for_clarification ? "Foreman is waiting on a super reply" : "No open clarifications"}
                  severity={(storyCounts?.buckets?.waiting_for_clarification || 0) > 0 ? "warning" : null}
                  filter="waiting_for_clarification" projectId={selectedId}
                  testid="ops-stories-waiting-clarification"
                  unavailable={storiesUnavailable} onRetry={retry}
                />
                <OperationsStoryRow
                  label="Recently Closed (7 days)"
                  count={storyCounts?.buckets?.recently_closed || 0}
                  hint={storyCounts?.buckets?.recently_closed ? "Field work closed in the last 7 days" : "No closures yet this week"}
                  severity={null}
                  filter="closed" projectId={selectedId}
                  testid="ops-stories-recently-closed"
                  unavailable={storiesUnavailable} onRetry={retry}
                />
              </>
            )}
            {/* Track 4 — Quantity Intelligence rollup row. */}
            {selectedId && (assetIntel || assetsUnavailable) && (
              <OperationsRow
                label={`Assets placed${assetIntel?.total ? "" : " — start tagging the drawing"}`}
                count={assetIntel?.total || 0}
                hint={
                  assetIntel?.total
                    ? `${(assetIntel.by_type[0]?.label) || "Assets"}: ${assetIntel.by_type[0]?.count || 0} · across ${assetIntel.by_sheet.length} sheets`
                    : "Place a manhole, inlet, valve, or sign on a sheet to begin"
                }
                severity={null}
                to={`/quantity-intelligence?project_id=${selectedId}`}
                testid="ops-assets-summary"
                unavailable={assetsUnavailable} onRetry={retry}
              />
            )}
            <OperationsRow
              label="Revisions needing review"
              count={reviewsNeeded}
              hint={reviewsNeeded ? "Open War Room to walk every change" : "Every revision has been reviewed"}
              severity={criticalChanges > 0 ? "critical" : reviewsNeeded > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/war-room` : null}
              testid="ops-revisions-review"
              unavailable={warRoomUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Revisions needing field notification"
              count={fieldNotifyNeeded}
              hint={fieldNotifyNeeded ? "Reviewed but foreman has not been sent the changes" : "Field has the latest"}
              severity={fieldNotifyNeeded > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/war-room` : null}
              testid="ops-revisions-notify"
              unavailable={warRoomUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Revisions needing acknowledgement"
              count={ackNeeded}
              hint={ackNeeded ? "Crew has not yet acknowledged" : "All revisions acknowledged"}
              severity={ackNeeded > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/war-room` : null}
              testid="ops-revisions-ack"
              unavailable={warRoomUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Pending clarifications"
              count={inboxClarifications}
              hint={inboxClarifications ? "Foreman is waiting on a super reply" : "No open clarifications"}
              severity={inboxClarifications > 0 ? "warning" : null}
              to={selectedId ? `/command?project_id=${selectedId}` : "/command"}
              testid="ops-inbox-clarifications"
              unavailable={warRoomUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Field conflicts"
              count={inboxConflicts}
              hint={inboxConflicts ? "Field reports the drawing doesn't match" : "No open field conflicts"}
              severity={inboxConflicts > 0 ? "critical" : null}
              to={selectedId ? `/command?project_id=${selectedId}` : "/command"}
              testid="ops-inbox-conflicts"
              unavailable={warRoomUnavailable} onRetry={retry}
            />
            {(warRoomUnavailable || (criticalChanges + highChanges) > 0) && (
              <OperationsRow
                label="Critical or high impact changes"
                count={criticalChanges + highChanges}
                hint={criticalChanges > 0 ? `${criticalChanges} critical change(s) in the field path` : "High impact items to confirm"}
                severity="critical"
                to={selectedId ? `/projects/${selectedId}/war-room` : null}
                testid="ops-revisions-risk"
                unavailable={warRoomUnavailable} onRetry={retry}
              />
            )}
            <OperationsRow
              label="Open punch items"
              count={openPunch}
              hint={openPunch ? "Tap to triage today's punch list" : "No outstanding punch items"}
              severity={openPunch > 5 ? "critical" : openPunch > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}?tab=punch` : null}
              testid="ops-punch"
              unavailable={punchUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Open revisions needing ack"
              count={openRevisions}
              hint={openRevisions ? "Crew has not yet acknowledged" : "All revisions acknowledged"}
              severity={openRevisions > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/revisions` : null}
              testid="ops-revisions"
              unavailable={revisionsUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Superseded sheets in plan set"
              count={ackCount}
              hint={ackCount ? "Older revisions still in circulation" : "Plan set is current"}
              severity={ackCount > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/revisions` : null}
              testid="ops-superseded"
              unavailable={trustUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Trust failures"
              count={trustFailures}
              hint={trustFailures ? "Categories blocking field use" : "Plan set ready"}
              severity={trustFailures > 0 ? "critical" : null}
              to={selectedId ? `/projects/${selectedId}` : null}
              testid="ops-trust"
              unavailable={trustUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Open findings"
              count={openFindings}
              hint={openFindings ? "Intake findings awaiting review" : "No outstanding findings"}
              severity={openFindings > 0 ? "warning" : null}
              to={selectedId ? `/projects/${selectedId}/intake` : null}
              testid="ops-findings"
              unavailable={findingsUnavailable} onRetry={retry}
            />
            <OperationsRow
              label="Open RFIs"
              count={0}
              hint="RFI surface lives in backlog"
              severity={null}
              testid="ops-rfis"
            />
            <OperationsRow
              label="Open safety actions"
              count={0}
              hint="Safety surface lives in backlog"
              severity={null}
              testid="ops-safety"
            />
          </>
        )}
      </div>

      {selectedProject && (
        <div className="mt-6">
          <Link
            to={`/projects/${selectedId}`}
            className="inline-flex items-center gap-2 text-sm text-[var(--fop-primary)] hover:text-[var(--fop-primary-hover)] min-h-[44px]"
            data-testid="ops-open-project"
          >
            Open {selectedProject.name}
            <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
}
